import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAYOUTS = [
  { v: "classic", lbl: "Classic", hint: "Centered serif header with double rule", colors: ["#0F172A", "#2563EB"] },
  { v: "corporate", lbl: "Corporate", hint: "Full-width dark banner with reversed text", colors: ["#0F172A", "#2563EB"] },
  { v: "minimal", lbl: "Minimal", hint: "Small logo, large left-aligned name, lots of whitespace", colors: ["#0F172A", "#94A3B8"] },
  { v: "elegant", lbl: "Elegant", hint: "Centered serif with hairline accent", colors: ["#0F172A", "#2563EB"] },
  { v: "azure_wave", lbl: "Azure Wave 3D", hint: "Layered sky-blue 3D waves top & bottom", colors: ["#0369A1", "#0EA5E9", "#7DD3FC"] },
  { v: "ocean_gradient", lbl: "Ocean Gradient", hint: "Deep blue gradient banner with glass circles", colors: ["#1E3A8A", "#3B82F6", "#93C5FD"] },
  { v: "sunset_ribbon", lbl: "Sunset Ribbon", hint: "Pink-orange gradient with golden ribbons", colors: ["#DB2777", "#F97316", "#FBBF24"] },
  { v: "emerald_geo", lbl: "Emerald Geometry", hint: "Green geometric triangles, corner accents", colors: ["#065F46", "#059669", "#6EE7B7"] },
  { v: "royal_gold", lbl: "Royal Gold", hint: "Navy banner with gold keylines — premium", colors: ["#1E293B", "#D4AF37", "#F6E27A"] },
  { v: "crimson_edge", lbl: "Crimson Edge 3D", hint: "Red angled banner with 3D drop shadow", colors: ["#7F1D1D", "#DC2626", "#FCA5A5"] },
  { v: "violet_flow", lbl: "Violet Flow 3D", hint: "Purple layered flowing curves, 3D depth", colors: ["#4C1D95", "#7C3AED", "#C4B5FD"] },
  { v: "teal_rail", lbl: "Teal Side Rail", hint: "Vertical teal rail down the page edge", colors: ["#134E4A", "#0D9488", "#5EEAD4"] },
  { v: "carbon_3d", lbl: "Carbon 3D", hint: "Dark carbon with amber beveled strip", colors: ["#111827", "#F59E0B", "#374151"] },
  { v: "rainbow_strip", lbl: "Rainbow Colorful", hint: "5-colour stripes top & bottom", colors: ["#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6"] },
  { v: "saffron_pride", lbl: "Saffron Pride", hint: "Saffron-white-green tricolor inspired", colors: ["#C2410C", "#F97316", "#16A34A"] },
  { v: "copper_luxe", lbl: "Copper Luxe", hint: "Charcoal with copper luxury lines", colors: ["#292524", "#B45309", "#FDBA74"] },
];

const EMPTY = {
  subject: "",
  body: "",
  layout: "classic",
  recipient_name: "",
  recipient_address: "",
  date: new Date().toISOString().split("T")[0],
};

export default function Letterheads() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/letterheads`); setList(r.data || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const startNew = () => { setEditing(null); setForm({ ...EMPTY }); };
  const startEdit = (lh) => {
    setEditing(lh.id);
    setForm({
      subject: lh.subject || "",
      body: lh.body || "",
      layout: lh.layout || "classic",
      recipient_name: lh.recipient_name || "",
      recipient_address: lh.recipient_address || "",
      date: lh.date || new Date().toISOString().split("T")[0],
    });
  };

  const save = async () => {
    if (!form.subject.trim() || !form.body.trim()) { alert("Subject and body are required."); return; }
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/letterheads/${editing}`, form);
      else await axios.post(`${API}/letterheads`, form);
      await refresh();
      setEditing(null); setForm({ ...EMPTY });
    } catch (e) { alert("Save failed: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const download = async (lh) => {
    try {
      const r = await axios.get(`${API}/letterheads/${lh.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `letter-${lh.letter_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Download failed: " + (e.response?.data?.detail || e.message)); }
  };
  const printLh = async (lh) => {
    try {
      const r = await axios.get(`${API}/letterheads/${lh.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url); if (w) setTimeout(() => w.print(), 800);
    } catch (e) { alert("Print failed: " + (e.response?.data?.detail || e.message)); }
  };
  const remove = async (lh) => {
    if (!window.confirm(`Delete letterhead ${lh.letter_no}?`)) return;
    try { await axios.delete(`${API}/letterheads/${lh.id}`); await refresh(); }
    catch (e) { alert("Delete failed: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="letterheads-page">
      {/* Sidebar list */}
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-slate-900">Saved Letterheads</h2>
          <Button type="button" onClick={startNew} size="sm" data-testid="new-letterhead-btn" className="bg-slate-900 hover:bg-slate-800 text-white text-xs">+ New</Button>
        </div>
        {loading ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : list.length === 0 ? (
          <p className="text-xs text-slate-400">No letterheads yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 -mx-4">
            {list.map(lh => (
              <li key={lh.id} className={`px-4 py-3 ${editing === lh.id ? "bg-blue-50" : ""}`}>
                <button type="button" data-testid={`open-lh-${lh.id}`} onClick={() => startEdit(lh)} className="text-left w-full">
                  <p className="text-sm font-semibold text-slate-900">{lh.subject || <em className="text-slate-400">Untitled</em>}</p>
                  <p className="text-xs text-slate-500">{lh.letter_no} &middot; {lh.date} &middot; <span className="capitalize">{lh.layout}</span></p>
                </button>
                <div className="mt-1 flex gap-2 text-xs">
                  <button type="button" data-testid={`download-lh-${lh.id}`} onClick={() => download(lh)} className="text-blue-700 font-semibold">Download</button>
                  <button type="button" data-testid={`print-lh-${lh.id}`} onClick={() => printLh(lh)} className="text-slate-700 font-semibold">Print</button>
                  <button type="button" data-testid={`delete-lh-${lh.id}`} onClick={() => remove(lh)} className="text-red-600 font-semibold ml-auto">Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editor */}
      <div className="lg:col-span-3 bg-white border border-slate-200 rounded-sm p-6 space-y-4">
        <h2 className="text-base font-bold text-slate-900">{editing ? "Edit Letterhead" : "Compose New Letterhead"}</h2>

        {/* Layout picker */}
        <div>
          <Label>Layout</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
            {LAYOUTS.map(l => (
              <button
                key={l.v}
                type="button"
                data-testid={`layout-${l.v}`}
                onClick={() => setForm({ ...form, layout: l.v })}
                className={`text-left p-3 rounded-sm border ${form.layout === l.v ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-400"}`}
              >
                <div className="flex h-2 w-full rounded-sm overflow-hidden mb-2">
                  {(l.colors || []).map((c, i) => (
                    <span key={i} className="flex-1" style={{ backgroundColor: c }} />
                  ))}
                </div>
                <p className="text-sm font-bold text-slate-900">{l.lbl}</p>
                <p className="text-[10px] text-slate-500 mt-1">{l.hint}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Date</Label>
            <Input type="date" data-testid="lh-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label>Subject *</Label>
            <Input data-testid="lh-subject" autoCap value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Confirmation of GST Returns Filed" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Recipient Name</Label>
            <Input data-testid="lh-recipient-name" autoCap value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="e.g. Mr. Patel" />
          </div>
          <div>
            <Label>Recipient Address</Label>
            <Textarea data-testid="lh-recipient-address" autoCap value={form.recipient_address} onChange={(e) => setForm({ ...form, recipient_address: e.target.value })} rows="2" placeholder="123 Industrial Road,&#10;Pune – 411001" />
          </div>
        </div>

        <div>
          <Label>Body *</Label>
          <Textarea data-testid="lh-body" autoCap value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows="10" placeholder="Dear Sir,&#10;&#10;…" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          {editing && (
            <Button type="button" onClick={startNew} variant="outline" size="sm" data-testid="clear-form-btn" className="text-xs">New</Button>
          )}
          <Button type="button" onClick={save} disabled={saving} data-testid="save-lh-btn" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
            {saving ? "Saving…" : (editing ? "Save changes" : "Save & generate")}
          </Button>
        </div>
        <p className="text-xs text-slate-400">After saving, use Download / Print on the left to get the PDF.</p>
      </div>
    </div>
  );
}
